import { DateTime } from 'luxon';

import {
  AccessorialCode,
  ShippingPointDetails,
  ZonedDateTimeRange,
} from '@rygen/rygen-ui/model';
import { loadLuxonDateTime } from '@rygen/rygen-ui/util';

// Import from the specific module, not the '@/composables' barrel: the barrel eagerly loads
// dozens of composables (several of which read model enums at module load), and pulling it from a
// model creates a model↔composables import cycle that breaks composable unit tests in isolation.
import { renderFullAddress } from '@/composables/Address';
import i18n from '@/i18n';

import { EmbeddableContact } from '../EmbeddableContact';
import { EmbeddableLocation } from '../EmbeddableLocation';
import { ReferenceNumber } from '../ReferenceNumber';
import { ShipmentStopType } from '../shipment';
import { ShippingPoint } from '../ShippingPoint';

const TYPE = 'Order';

export class OrderShippingPoint implements ShippingPoint {
  id?: number;
  stopType: ShipmentStopType.PICKUP | ShipmentStopType.DROP_OFF =
    ShipmentStopType.PICKUP;
  location: EmbeddableLocation = new EmbeddableLocation();
  shippingPointDetails: ShippingPointDetails = new ShippingPointDetails();
  contact: EmbeddableContact = new EmbeddableContact();
  requestedAppointmentEarliest?: DateTime;
  requestedAppointmentLatest?: DateTime;
  requestedAppointmentDateLock: boolean = false;
  requestedAppointmentTimeLock: boolean = false;
  requestedAccessorials: AccessorialCode[] = [];
  orderReferenceNumbers: ReferenceNumber[] = [];
  saveToAddressBook: boolean = false;

  constructor(opts?: Partial<OrderShippingPoint>) {
    if (opts?.id != null) {
      this.id = opts.id;
    }
    if (typeof opts?.stopType === 'string') {
      this.stopType = ShipmentStopType[opts.stopType];
    } else if (opts?.stopType) {
      this.stopType = opts.stopType;
    }
    if (opts?.location != null) {
      this.location = new EmbeddableLocation(opts.location);
    }
    if (opts?.shippingPointDetails != null) {
      this.shippingPointDetails = new ShippingPointDetails(
        opts.shippingPointDetails,
      );
    }
    if (opts?.contact != null) {
      this.contact = new EmbeddableContact(opts.contact);
    }
    this.requestedAppointmentEarliest = loadLuxonDateTime(
      opts?.requestedAppointmentEarliest,
      opts?.location?.timeZone,
    );
    this.requestedAppointmentLatest = loadLuxonDateTime(
      opts?.requestedAppointmentLatest,
      opts?.location?.timeZone,
    );
    if (opts?.requestedAccessorials != null) {
      this.requestedAccessorials = opts.requestedAccessorials.map(
        (val) => new AccessorialCode(val),
      );
    }
    if (opts?.orderReferenceNumbers != null) {
      this.orderReferenceNumbers = opts.orderReferenceNumbers.map(
        (val) => new ReferenceNumber(val),
      );
    }
    if (opts?.saveToAddressBook != null) {
      this.saveToAddressBook = opts.saveToAddressBook;
    }
    if (opts?.requestedAppointmentDateLock != null) {
      this.requestedAppointmentDateLock = opts.requestedAppointmentDateLock;
    }
    if (opts?.requestedAppointmentTimeLock != null) {
      this.requestedAppointmentTimeLock = opts.requestedAppointmentTimeLock;
    }
  }

  toString(): string {
    return renderFullAddress(this.location?.toAddress());
  }

  static isOrderShippingPoint(
    location: ShippingPoint,
  ): location is OrderShippingPoint {
    return location.type === TYPE;
  }

  get type() {
    return TYPE;
  }

  get isInProgress() {
    return false;
  }

  get isPickup() {
    return this.stopType === ShipmentStopType.PICKUP;
  }

  get isDropOff() {
    return this.stopType === ShipmentStopType.DROP_OFF;
  }

  get requestedAppointmentRange() {
    return new ZonedDateTimeRange({
      start: this.requestedAppointmentEarliest,
      end: this.requestedAppointmentLatest,
    });
  }

  get sequenceNumber() {
    return this.stopType === ShipmentStopType.PICKUP ? 1 : 2;
  }

  get stopLabel(): string {
    const stopName = [
      this.location.locationName,
      this.location.city,
      this.location.state,
    ]
      .filter((x) => !!x?.trim())
      .join(', ');

    const stopNumber = i18n.global.t('common.paramNum', {
      param: i18n.global.t('shipments.stops'),
      num: this.sequenceNumber,
    });

    return [stopNumber, stopName].join(' - ');
  }

  isResolved(_requireBothDate: boolean = false) {
    return false;
  }

  toJSON() {
    // `JSON.stringify` ignores getters so manually add them here if needed
    return {
      ...this,
      sequenceNumber: this.sequenceNumber,
    };
  }
}
